import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import { chartTotal } from '@/lib/dashboard/dashboardChartData'
import { cn } from '@/lib/utils'

/**
 * @param {{
 *   data: { name: string, value: number, color: string }[],
 *   centerLabel?: string,
 *   centerValue?: string | number,
 *   emptyLabel?: string,
 *   className?: string,
 *   height?: number,
 * }} props
 */
export function DashboardDonut({
  data,
  centerLabel = 'Total',
  centerValue,
  emptyLabel = 'No data yet',
  className,
  height = 168,
}) {
  const total = chartTotal(data)
  const displayCenter = centerValue ?? total

  if (total === 0) {
    return (
      <div
        className={cn('flex items-center justify-center text-sm text-muted-foreground', className)}
        style={{ height }}
      >
        {emptyLabel}
      </div>
    )
  }

  return (
    <div className={cn('relative w-full', className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="58%"
            outerRadius="82%"
            paddingAngle={3}
            strokeWidth={0}
          >
            {data.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              borderRadius: 12,
              fontSize: 12,
              border: 'none',
              boxShadow: '0 4px 24px rgba(124, 58, 237, 0.12)',
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {centerLabel}
        </span>
        <span className="text-xl font-bold tabular-nums tracking-tight text-foreground">
          {displayCenter}
        </span>
      </div>
    </div>
  )
}

/**
 * @param {{ data: { name: string, value: number, color: string }[] }} props
 */
export function DashboardDonutLegend({ data }) {
  if (data.length === 0) return null

  return (
    <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
      {data.map((row) => (
        <li key={row.name} className="flex min-w-0 items-center gap-1.5 text-[11px]">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: row.color }}
            aria-hidden
          />
          <span className="truncate text-muted-foreground">{row.name}</span>
          <span className="ml-auto font-semibold tabular-nums text-foreground">{row.value}</span>
        </li>
      ))}
    </ul>
  )
}
